import autobind from 'core-decorators/lib/autobind';
import classnames from 'classnames';

import { WixConnect } from 'shared/utils/wix-connect';
import memoizedPartial from 'shared/utils/memoized-partial';
import WixSDK from 'shared/utils/wix-sdk';
import pubsub from 'shared/utils/pubsub';

import modalTypes from 'shared/constants/modal-types';
import deviceTypes from 'shared/constants/device-types';

import Header, { DangerModalHeader, IFrameModalHeader } from './header/header';
import HeaderMobile from './header/header-mobile';
import {
  PrimaryButton,
  SecondaryButton,
  DangerPrimaryButton,
  DangerSecondaryButton,
} from 'shared/components/button/button';
import FixedInIFrame from 'mobile-overlay/components/fixed-in-iframe/fixed-in-iframe';

import events from 'shared/constants/events';

import styles from './modal.styl';
import stylesMobile from './modal-mobile.styl';


const closeOnOverlayClick = false;
export const DISMISSED = Symbol('dismissed');
export const CANCELLED = Symbol('cancelled');

export class ModalBase extends React.Component {

  static propTypes = {
    title: React.PropTypes.any,
    content: React.PropTypes.any,
    className: React.PropTypes.string,
    windowClassName: React.PropTypes.string,
    contentClassName: React.PropTypes.string,
    buttonsClassName: React.PropTypes.string,
    closeOnOverlayClick: React.PropTypes.bool,
  };

  static defaultProps = {
    closeOnOverlayClick,
  };

  state = {
    open: false,
    closeOnOverlayClick,
    title: null,
    content: null,
  };

  componentWillMount() {
    this.openModalUnsubscribe = pubsub.subscribe(events.MODAL.OPEN, this.openModal);
    this.closeModalUnsubscribe = pubsub.subscribe(events.MODAL.CLOSE, this.closeModal);
  }

  componentWillUnmount() {
    this.openModalUnsubscribe();
    this.closeModalUnsubscribe();
  }

  @autobind
  openModal(config) {
    this.setState({
      title: null,
      content: null,
      Header,
      closeOnOverlayClick,
      className: '',
      windowClassName: '',
      contentClassName: '',
      buttonsClassName: '',
      buttons: {}, // {primary: {label, onClick, Button}, secondary: {...}}

      resolve: _.noop,
      reject: _.noop,

      modalType: modalTypes.POPUP,
      noCloseButton: false,
      ...config,
    });

    setTimeout(() => {
      this.setState({
        open: true,
      });
    }, 0);
  }

  @autobind
  closeModal() {
    return new Promise(resolve => {
      this.setState({ open: false }, () => {
        setTimeout(() => {
          this.setState({ content: null }, resolve);
        }, 200);
      });
    });
  }

  @autobind
  handleBackdropClick() {
    const { closeOnOverlayClick } = this.state;
    if (closeOnOverlayClick) {
      this.$modal.dismiss();
    }
  }

  @autobind
  handleCloseButtonClick() {
    this.$modal.dismiss();
  }

  stopEventPropagation(event) {
    event.stopPropagation();
  }

  get $modal() {
    const { resolve, reject } = this.state;

    return {
      resolve: data => {
        resolve({ data, $modal: this.$modal });
      },
      reject(reason = DISMISSED) {
        reject(reason);
      },
      close: data => {
        this.closeModal().then(() => resolve(data));
      },
      dismiss: (reason = DISMISSED) => {
        this.closeModal().then(() => reject(reason));
      },
    };
  }

  render() {
    return null;
  }
}


@WixConnect(styles)
export class SimpleModalContent extends React.Component {
  static propTypes = {
    children: React.PropTypes.any,
  };

  render() {
    return (
      <div styleName="simple-modal-content">
        {this.props.children}
      </div>
    );
  }
}


@WixConnect(styles)
export default class Modal extends ModalBase {
  static displayName = 'Modal';

  get buttons() {
    const { buttons, buttonsClassName } = this.state;

    if (!_.keys(buttons).length) {
      return null;
    }

    return (
      <div styleName="buttons"
           className={buttonsClassName}>
        {
          _.map(buttons, ({ Button, label, onClick }, key) => (
            <Button key={key}
                    label={label}
                    onClick={memoizedPartial(onClick, this.$modal)}
                    styleName="button" />
          ))
        }
      </div>
    );
  }

  render() {
    const { open, content, modalType } = this.state;

    if (modalType !== modalTypes.POPUP) {
      return null;
    }

    if (!open && !content) {
      return null;
    }

    const { Header: InheritHeader, title, className, windowClassName, contentClassName } = this.state;

    const styleNames = classnames('modal', {
      open: open && content,
    });

    return (
      <div styleName={styleNames}
           className={className}
           onClick={this.handleBackdropClick}>
        <div styleName="window"
             className={windowClassName}
             onClick={this.stopEventPropagation}>
          <InheritHeader title={title}
                         onButtonClick={this.handleCloseButtonClick} />
          <section styleName="content"
                   className={contentClassName}>
            {React.cloneElement(content, { $modal: this.$modal })}
          </section>
          {this.buttons}
        </div>
      </div>
    );
  }
}


export const MobileModal = WixConnect(stylesMobile)(props => (
  <FixedInIFrame styleName="fixed-iframe">
    <Modal {...props} />
  </FixedInIFrame>
));


function injectButtonComponentsIntoConfig(config, buttonComponents) {
  let { buttons } = config;

  buttons = _.mapValues(buttons, function (value, key) {
    if (value.Button) {
      return value;
    }

    return { ...value, Button: buttonComponents[key] };
  });

  return { ...config, buttons };
}


let modalPromise;
export function getModalPromise() {
  return modalPromise;
}


export function isClose(reason) {
  return reason === DISMISSED;
}

export function closeCatcher(reason) {
  if (isClose(reason)) {
    return;
  }
  return Promise.reject(reason);
}


export function openModal(config) {
  config = injectButtonComponentsIntoConfig(config, {
    primary: PrimaryButton,
    secondary: SecondaryButton,
  });

  if (WixSDK.Utils.getDeviceType() === deviceTypes.MOBILE) {
    config = {
      Header: HeaderMobile,
      className: stylesMobile['modal-mobile'],
      windowClassName: stylesMobile['modal-mobile-window'],
      contentClassName: stylesMobile['modal-mobile-content'],
      closeOnOverlayClick: true,
      ...config,
    };
  }

  modalPromise = new Promise(function (resolve, reject) {
    pubsub.publish(events.MODAL.OPEN, {
      ...config,
      resolve,
      reject,
    });
  });

  return modalPromise;
}


export function openDangerModal(config) {
  config = injectButtonComponentsIntoConfig(config, {
    primary: DangerPrimaryButton,
    secondary: DangerSecondaryButton,
  });

  return openModal({
    ...config,
    Header: DangerModalHeader,
  });
}


export function openIFrameModal(url, { windowClassName, contentClassName, ...otherConfig } = {}) {
  return openModal({
    ...otherConfig,
    windowClassName: classnames(styles['iframe-modal-window'], windowClassName),
    contentClassName: classnames(styles['iframe-modal-content'], contentClassName),
    Header: IFrameModalHeader,
    content: (
      <SimpleModalContent>
        <iframe src={url}
                className={styles.iframe} />
      </SimpleModalContent>
    ),
  });
}


export function closeModal() {
  pubsub.publish(events.MODAL.CLOSE);
}



// WEBPACK FOOTER //
// ./src/shared/components/modal/modal.js